import { useCallback, useEffect, useMemo, useState } from "react";
import { IceAktarYolu } from "../kabiliyet/IceAktarYolu";
import { M } from "../dil";
import { routeSorgusunuAl } from "../lib/router";
import { HesapAdi, hesapSeridi } from "../ui/HesapAdi";
import type { Account, TransactionFilter } from "@parafon/contracts/local-api";
import type { Transaction, TransactionType } from "@parafon/contracts";
import { MIKTAR_HANE, formatDecimal } from "../lib/decimal";
import {
  TRANSACTION_TYPES,
  TRANSACTION_TYPE_LABEL,
  hareketAdi,
  assetLabel,
  formatCount,
  formatIsoDate,
} from "../lib/format";
import { TX_SHAPE, quantitySign } from "../lib/txShape";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync, useMutation } from "../repo/useAsync";
import { useHesapErisimi } from "../kabiliyet/useHesapErisimi";
import { TransactionForm } from "../components/TransactionForm";
import { Confirm, Modal } from "../ui/Modal";
import { TarihAraligiSecici } from "../ui/TarihAraligiSecici";
import {
  Button,
  EmptyState,
  ErrorBlock,
  Field,
  InlineError,
  LoadingBlock,
  NumberCell,
  Screen,
  Select,
  Spinner,
  Tag,
  TextInput,
} from "../ui/primitives";

const PAGE_SIZES = [25, 50, 100, 250] as const;

interface FilterState {
  readonly accountId: string;
  readonly type: string;
  readonly from: string;
  readonly to: string;
  readonly text: string;
}

const EMPTY_FILTER: FilterState = { accountId: "", type: "", from: "", to: "", text: "" };

export function TransactionsScreen() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [editing, setEditing] = useState<Transaction | "new" | null>(null);
  // Kasa açılınca ilk sayfa buysa giriş formu açık gelir (`lib/acilisSayfasi.ts`).
  useEffect(() => {
    if (routeSorgusunuAl("yeni") === "1") setEditing("new");
  }, []);
  const [deleting, setDeleting] = useState<Transaction | null>(null);
  const remove = useMutation();

  const accountsLoad = useCallback(() => repo.accounts.list(false), [repo]);
  const accounts = useAsync(accountsLoad, [repo, revision]);
  // Gelir modeli: süre bitince sınırı aşan hesaplar salt okunur.
  const erisim = useHesapErisimi();

  const query: TransactionFilter = useMemo(
    () => ({
      ...(filter.accountId === "" ? {} : { accountIds: [filter.accountId] }),
      ...(filter.type === "" ? {} : { types: [filter.type as TransactionType] }),
      ...(filter.from === "" ? {} : { from: filter.from }),
      ...(filter.to === "" ? {} : { to: filter.to }),
      limit: pageSize,
      offset: page * pageSize,
    }),
    [filter.accountId, filter.type, filter.from, filter.to, page, pageSize],
  );

  const txLoad = useCallback(() => repo.transactions.query(query), [repo, query]);
  const { state, reload } = useAsync(txLoad, [repo, query, revision]);

  const hesapById = useMemo(() => {
    const map = new Map<string, Account>();
    for (const a of accounts.state.data ?? []) map.set(a.id, a);
    return map;
  }, [accounts.state.data]);

  // Serbest metin araması sözleşmede yok (bkz. TransactionFilter) — bu yüzden
  // yalnızca GELEN SAYFA üzerinde çalışır ve arayüz bunu açıkça söyler.
  const needle = filter.text.trim().toLocaleLowerCase("tr");
  const rows = useMemo(() => {
    const all = state.data?.rows ?? [];
    if (needle === "") return all;
    return all.filter(
      (t) =>
        assetLabel(t.asset).toLocaleLowerCase("tr").includes(needle) ||
        (t.note ?? "").toLocaleLowerCase("tr").includes(needle),
    );
  }, [state.data, needle]);

  const total = state.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const anyFilter =
    filter.accountId !== "" || filter.type !== "" || filter.from !== "" || filter.to !== "" || filter.text !== "";

  const patch = (p: Partial<FilterState>): void => {
    setFilter((f) => ({ ...f, ...p }));
    setPage(0);
  };

  const onDelete = async (): Promise<void> => {
    if (deleting === null) return;
    const ok = await remove.run(() => repo.transactions.remove(deleting.id));
    if (ok) {
      setDeleting(null);
      invalidate();
    }
  };

  return (
    <Screen
      title={M.hareketler.ekran.baslik}
      description={M.hareketler.ekran.aciklama}
      toolbar={
        <>
          {state.status === "loading" && state.data !== undefined && <Spinner label={M.hareketler.ekran.yenileniyor} />}
          <Button variant="primary" onClick={() => setEditing("new")} disabled={(accounts.state.data ?? []).length === 0}>
            {M.hareketler.ekran.hareketEkle}
          </Button>
          <IceAktarYolu />
        </>
      }
    >
      {/* --- filtreler --- */}
      <form
        aria-label={M.hareketler.ekran.filtreler}
        data-kart-bolum="form"
        className="grid gap-3 border-b border-line px-4 py-3 sm:grid-cols-2 lg:grid-cols-6"
        onSubmit={(e) => e.preventDefault()}
      >
        <Field label={M.hareketler.ekran.hesap} htmlFor="f-account">
          <Select
            id="f-account"
            value={filter.accountId}
            onChange={(e) => patch({ accountId: e.currentTarget.value })}
            placeholder={M.ortak.tumu}
            options={(accounts.state.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
          />
        </Field>

        <Field label={M.hareketler.ekran.tur} htmlFor="f-type">
          <Select
            id="f-type"
            value={filter.type}
            onChange={(e) => patch({ type: e.currentTarget.value })}
            placeholder={M.ortak.tumu}
            options={TRANSACTION_TYPES.map((t) => ({ value: t, label: TRANSACTION_TYPE_LABEL[t] }))}
          />
        </Field>

        <TarihAraligiSecici
          idOnek="f"
          from={filter.from}
          to={filter.to}
          onChange={(a) => patch({ from: a.from, to: a.to })}
          bosOlabilir
        />

        <Field label={M.hareketler.ekran.ara} htmlFor="f-text" hint={M.hareketler.ekran.araIpucu}>
          <TextInput
            id="f-text"
            value={filter.text}
            placeholder={M.hareketler.ekran.araOrnek}
            onChange={(e) => patch({ text: e.currentTarget.value })}
          />
        </Field>

        {anyFilter && (
          <div className="sm:col-span-2 lg:col-span-6">
            <Button onClick={() => { setFilter(EMPTY_FILTER); setPage(0); }}>{M.hareketler.ekran.filtreleriTemizle}</Button>
          </div>
        )}
      </form>

      {state.status === "loading" && state.data === undefined && <LoadingBlock label={M.hareketler.ekran.yukleniyor} />}
      {state.status === "error" && <ErrorBlock error={state.error} onRetry={reload} />}

      {state.data !== undefined &&
        (rows.length === 0 ? (
          <EmptyState
            title={anyFilter ? M.hareketler.ekran.filtreyeUyanYok : M.hareketler.ekran.henuzYok}
            description={
              anyFilter
                ? M.hareketler.ekran.filtreleriGevset
                : M.hareketler.ekran.henuzYokMetin
            }
            action={
              anyFilter ? (
                <Button onClick={() => { setFilter(EMPTY_FILTER); setPage(0); }}>{M.hareketler.ekran.filtreleriTemizle}</Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setEditing("new")}
                  disabled={(accounts.state.data ?? []).length === 0}
                >
                  {M.hareketler.ekran.ilkHareket}
                </Button>
              )
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="pf-table" data-kart-bolum="liste">
                <caption className="sr-only">{M.hareketler.ekran.liste}</caption>
                <thead>
                  <tr>
                    <th scope="col">{M.hareketler.ekran.tarih}</th>
                    <th scope="col">{M.hareketler.ekran.hesap}</th>
                    <th scope="col">{M.hareketler.ekran.tur}</th>
                    <th scope="col">{M.hareketler.ekran.varlik}</th>
                    <th scope="col" className="pf-num">
                      {M.hareketler.ekran.miktar}
                    </th>
                    <th scope="col" className="pf-num">
                      {M.hareketler.ekran.fiyatTutar}
                    </th>
                    <th scope="col" className="pf-num">
                      {M.hareketler.ekran.masraf}
                    </th>
                    <th scope="col" className="pf-num">
                      {M.hareketler.ekran.vergi}
                    </th>
                    <th scope="col" className="w-32 text-right">
                      {M.hareketler.ekran.islem}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => {
                    const shape = TX_SHAPE[t.type];
                    return (
                      <tr key={t.id}>
                        <td className="pf-fig whitespace-nowrap" style={hesapSeridi(hesapById.get(t.accountId)?.renk)}>
                          {formatIsoDate(t.tradeDate)}
                        </td>
                        <td className="max-w-40 truncate">
                          <HesapAdi renk={hesapById.get(t.accountId)?.renk} renksiz="text-muted">
                            {hesapById.get(t.accountId)?.name ?? t.accountId}
                          </HesapAdi>
                        </td>
                        <td>
                          <Tag>{hareketAdi(t)}</Tag>
                        </td>
                        <td>
                          <span className="text-ink">{assetLabel(t.asset)}</span>
                          <span className="ml-1.5 text-[10px] text-faint">
                            {t.asset.kind === "tracked" ? M.hareketler.ekran.izlenen : M.hareketler.ekran.elle}
                          </span>
                        </td>
                        <td className="pf-num">
                          {shape.amount ? (
                            <span className="text-faint">—</span>
                          ) : (
                            <NumberCell sign={quantitySign(t.type)}>
                              {formatDecimal(t.quantity, { maxFractionDigits: MIKTAR_HANE })}
                            </NumberCell>
                          )}
                        </td>
                        <td className="pf-num">
                          <NumberCell currency={t.currency}>{formatDecimal(t.price, { fractionDigits: 2 })}</NumberCell>
                          {t.type === "FX_CONVERSION" && t.alinanTutar !== undefined && t.alinanParaBirimi !== undefined && (
                            <span className="block text-[11px] text-muted">
                              → <NumberCell currency={t.alinanParaBirimi}>{formatDecimal(t.alinanTutar, { fractionDigits: 2 })}</NumberCell>
                            </span>
                          )}
                        </td>
                        <td className="pf-num text-muted">{formatDecimal(t.fee, { fractionDigits: 2 })}</td>
                        <td className="pf-num text-muted">{formatDecimal(t.tax, { fractionDigits: 2 })}</td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              className="!h-6 !px-2 text-[12px]"
                              disabled={!erisim.duzenlenebilirMi(t.accountId)}
                              title={erisim.duzenlenebilirMi(t.accountId) ? undefined : M.hareketler.ekran.saltOkunur}
                              onClick={() => setEditing(t)}
                            >
                              {M.ortak.duzenle}
                            </Button>
                            <Button
                              variant="danger"
                              className="!h-6 !px-2 text-[12px]"
                              disabled={!erisim.duzenlenebilirMi(t.accountId)}
                              onClick={() => setDeleting(t)}
                            >
                              {M.ortak.sil}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* --- sayfalama --- */}
            <nav
              aria-label={M.hareketler.ekran.sayfalama}
              className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-2 text-[12px] text-muted"
            >
              <p>
                {M.hareketler.ekran.toplamOnEk}
                <span className="pf-num text-ink">{formatCount(total)}</span>
                {M.hareketler.ekran.kayitSayfa(total)}{" "}
                <span className="pf-num text-ink">{formatCount(page + 1)}</span> /{" "}
                <span className="pf-num">{formatCount(pageCount)}</span>
                {needle !== "" && <span>{M.hareketler.ekran.aramaSatir(formatCount(rows.length))}</span>}
              </p>
              <div className="flex items-center gap-2">
                <label htmlFor="page-size" className="text-[12px]">
                  {M.hareketler.ekran.sayfaBoyu}
                </label>
                <Select
                  id="page-size"
                  className="!h-7 w-20"
                  value={String(pageSize)}
                  onChange={(e) => {
                    setPageSize(Number(e.currentTarget.value));
                    setPage(0);
                  }}
                  options={PAGE_SIZES.map((s) => ({ value: String(s), label: String(s) }))}
                />
                <Button className="!h-7" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  {M.hareketler.ekran.onceki}
                </Button>
                <Button
                  className="!h-7"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                >
                  {M.hareketler.ekran.sonraki}
                </Button>
              </div>
            </nav>
          </>
        ))}

      <Modal
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editing === "new" ? M.hareketler.ekran.hareketEkle : M.hareketler.ekran.hareketiDuzenle}
        description={M.hareketler.ekran.formAciklama}
      >
        {editing !== null && (
          <TransactionForm
            accounts={(accounts.state.data ?? []).filter((a) => erisim.duzenlenebilirMi(a.id))}
            transaction={editing === "new" ? null : editing}
            onDone={() => {
              setEditing(null);
              invalidate();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      <Confirm
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={M.hareketler.ekran.silOnayBaslik}
        body={
          <>
            <p>
              {deleting !== null && (
                <>
                  {formatIsoDate(deleting.tradeDate)} · {hareketAdi(deleting)} ·{" "}
                  {assetLabel(deleting.asset)}
                </>
              )}
            </p>
            <p className="mt-2">
              {M.hareketler.ekran.silOnayMetin}
            </p>
            <InlineError error={remove.error} />
          </>
        }
        confirmLabel={M.ortak.sil}
        onConfirm={() => void onDelete()}
        pending={remove.pending}
      />
    </Screen>
  );
}
